import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import continuousServiceService from '../../services/continuousServiceService';
import { serviceService } from '../../services/serviceService';
import { getProjects } from '../../services/projectsService';
import { getAreas } from '../../services/areaService';
import ServiceSelector from '../../components/ServiceOrders/ServiceSelector';
import ConfirmModal from '../../components/Common/ConfirmModal';
import CloseServiceModal from './CloseServiceModal';
import Toast from '../../components/Common/Toast';
import { formatDateLocaleLima, formatDateToLima } from '../../utils/dateUtils';

const roundTo4 = (v) => (Math.round(Number(v) * 10000) / 10000);
const sanitizeDecimalInput = (value) => String(value ?? '')
  .replace(',', '.')
  .replace(/[^\d.]/g, '')
  .replace(/(\..*)\./g, '$1');

const ContinuousServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  
  // Estados para modales
  const [isAddProgressModalOpen, setIsAddProgressModalOpen] = useState(false);
  const [isEditProgressModalOpen, setIsEditProgressModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [isDeleteProgressModalOpen, setIsDeleteProgressModalOpen] = useState(false);
  const [isGenerateDocumentModalOpen, setIsGenerateDocumentModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState(null);
  const [progressToDelete, setProgressToDelete] = useState(null);
  
  // Estados para importación
  const [importData, setImportData] = useState([]);
  const [importErrors, setImportErrors] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  
  // Estados para formulario de registro de avance
  const [progressForm, setProgressForm] = useState({
    progress_date: new Date().toISOString().split('T')[0],
    service_id: '',
    project_id: '',
    area_id: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    tax_rate: 18, // 0, 10, 10.5 o 18
  });

  const [progressErrors, setProgressErrors] = useState({});
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const lastLoadedId = useRef(null);

  // Edición de cabeceras (Información General)
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ start_date: '', end_date: '', notes: '', include_areas: false });
  const [savingHeader, setSavingHeader] = useState(false);

  // Ajuste de montos (IGV / Total por diferencias de decimales)
  const [isEditingAmounts, setIsEditingAmounts] = useState(false);
  const [amountForm, setAmountForm] = useState({ igv_amount: '', total_amount: '' });
  const [savingAmounts, setSavingAmounts] = useState(false);

  useEffect(() => {
    // Solo cargar si el id cambió (evita doble llamada en StrictMode)
    if (lastLoadedId.current !== id) {
      lastLoadedId.current = id;
      loadService();
      loadServices();
      loadProjects();
    }
  }, [id]);

  useEffect(() => {
    if (service?.include_areas) {
      getAreas({ active_only: true }).then((res) => {
        if (res.success && res.data) setAreas(res.data);
      }).catch(() => setAreas([]));
    } else {
      setAreas([]);
    }
  }, [service?.include_areas]);

  const loadServices = async () => {
    try {
      const servicesRes = await serviceService.getServices({ 
        active_only: true,
        service_type: 'acquisition'
      });
      if (servicesRes.success) {
        setServices(servicesRes.data || []);
      }
    } catch (error) {
      console.error('Error al cargar servicios:', error);
    }
  };

  const loadProjects = async () => {
    try {
      const projectsRes = await getProjects({ active_only: true });
      if (projectsRes.success) {
        setProjects(projectsRes.data || []);
      }
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
    }
  };

  const sortProgressByDateAsc = (list) => {
    if (!list || !list.length) return list;
    return [...list].sort((a, b) => {
      const dateA = new Date(a.progress_date || 0);
      const dateB = new Date(b.progress_date || 0);
      return dateA - dateB; // del más antiguo al más nuevo
    });
  };

  const loadService = async () => {
    setLoading(true);
    try {
      const response = await continuousServiceService.getContinuousService(id);
      if (response.success) {
        setService(response.data);
        setProgress(sortProgressByDateAsc(response.data.progress || []));
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar el servicio continuo',
          type: 'error'
        });
        setTimeout(() => navigate('/continuous-services'), 2000);
      }
    } catch (error) {
      console.error('Error al cargar servicio continuo:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar el servicio continuo',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatCurrency4 = (amount) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { label: 'Abierto', className: 'bg-green-100 text-green-800' },
      closed: { label: 'Cerrado', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleAddProgress = () => {
    setProgressForm({
      progress_date: new Date().toISOString().split('T')[0],
      service_id: '',
      project_id: '',
      area_id: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      tax_rate: 18,
    });
    setProgressErrors({});
    setIsAddProgressModalOpen(true);
  };

  const handleEditProgress = (progressItem) => {
    setSelectedProgress(progressItem);
    
    // Formatear la fecha para el input date (YYYY-MM-DD)
    const formattedDate = formatDateToLima(progressItem.progress_date) || new Date().toISOString().split('T')[0];
    
    setProgressForm({
      progress_date: formattedDate,
      service_id: progressItem.service_id || '',
      project_id: progressItem.project_id || '',
      area_id: progressItem.area_id || '',
      description: progressItem.description || '',
      quantity: roundTo4(progressItem.quantity),
      unit_price: roundTo4(progressItem.unit_price),
      tax_rate: progressItem.tax_rate || (progressItem.has_igv !== false ? 18 : 0),
    });
    setProgressErrors({});
    setIsEditProgressModalOpen(true);
  };

  const handleDeleteProgress = (progressId) => {
    setProgressToDelete(progressId);
    setIsDeleteProgressModalOpen(true);
  };

  const handleConfirmDeleteProgress = async () => {
    if (!progressToDelete) return;

    setLoadingAction(true);
    try {
      const response = await continuousServiceService.deleteProgress(id, progressToDelete);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Registro de avance eliminado exitosamente',
          type: 'success'
        });
        setIsDeleteProgressModalOpen(false);
        setProgressToDelete(null);
        loadService();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al eliminar el registro',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al eliminar registro:', error);
      setToast({
        isOpen: true,
        message: 'Error al eliminar el registro de avance',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const calculateProgressTotals = () => {
    const subtotal = parseFloat(progressForm.quantity || 0) * parseFloat(progressForm.unit_price || 0);
    const taxRate = parseFloat(progressForm.tax_rate || 0);
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    return { subtotal, taxAmount, total, taxRate };
  };

  const handleSaveProgress = async () => {
    const newErrors = {};
    if (!progressForm.progress_date) newErrors.progress_date = 'La fecha es requerida';
    if (!progressForm.quantity || parseFloat(progressForm.quantity) <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a 0';
    }
    if (!progressForm.unit_price || parseFloat(progressForm.unit_price) < 0) {
      newErrors.unit_price = 'El precio unitario es requerido';
    }

    setProgressErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoadingAction(true);
    try {
      const totals = calculateProgressTotals();
      const submitData = {
        progress_date: progressForm.progress_date,
        service_id: progressForm.service_id ? parseInt(progressForm.service_id) : null,
        project_id: progressForm.project_id ? parseInt(progressForm.project_id) : null,
        area_id: service?.include_areas && progressForm.area_id ? parseInt(progressForm.area_id) : null,
        description: progressForm.description || null,
        quantity: roundTo4(progressForm.quantity),
        unit_price: roundTo4(progressForm.unit_price),
        has_igv: totals.taxRate > 0,
        subtotal: totals.subtotal,
        tax_rate: totals.taxRate,
        tax_amount: totals.taxAmount,
        total: totals.total,
      };

      let response;
      if (isEditProgressModalOpen && selectedProgress) {
        response = await continuousServiceService.updateProgress(id, selectedProgress.id, submitData);
      } else {
        response = await continuousServiceService.createProgress(id, submitData);
      }

      if (response.success) {
        setToast({
          isOpen: true,
          message: isEditProgressModalOpen 
            ? 'Registro de avance actualizado exitosamente'
            : 'Registro de avance creado exitosamente',
          type: 'success'
        });
        setIsAddProgressModalOpen(false);
        setIsEditProgressModalOpen(false);
        setSelectedProgress(null);
        loadService();
      } else {
        setProgressErrors(response.errors || {});
        setToast({
          isOpen: true,
          message: response.message || 'Error al guardar el registro',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al guardar registro:', error);
      setToast({
        isOpen: true,
        message: 'Error al guardar el registro de avance',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCloseService = async (endDate) => {
    if (!endDate) return;
    setLoadingAction(true);
    try {
      const response = await continuousServiceService.closeContinuousService(id, { end_date: endDate });
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Servicio continuo cerrado exitosamente',
          type: 'success'
        });
        setIsCloseModalOpen(false);
        loadService();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cerrar el servicio',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cerrar servicio:', error);
      setToast({
        isOpen: true,
        message: 'Error al cerrar el servicio continuo',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenService = async () => {
    setLoadingAction(true);
    try {
      const response = await continuousServiceService.openContinuousService(id);
      if (response.success) {
        setToast({
          isOpen: true,
          message: 'Servicio continuo abierto exitosamente',
          type: 'success'
        });
        loadService();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al abrir el servicio',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al abrir servicio:', error);
      setToast({
        isOpen: true,
        message: 'Error al abrir el servicio continuo',
        type: 'error'
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenAmounts = () => {
    const igv = service?.igv_amount != null ? Number(service.igv_amount) : (serviceTotals?.igv18 || 0) + (serviceTotals?.igv10 || 0);
    const total = service?.total_amount != null ? Number(service.total_amount) : serviceTotals?.total ?? 0;
    setAmountForm({
      igv_amount: String(igv),
      total_amount: String(total),
    });
    setIsEditingAmounts(true);
  };

  const handleSaveAmounts = async () => {
    const igvNum = parseFloat(amountForm.igv_amount);
    if (Number.isNaN(igvNum) || igvNum < 0) {
      setToast({ isOpen: true, message: 'Ingrese un IGV válido (número ≥ 0)', type: 'error' });
      return;
    }
    setSavingAmounts(true);
    try {
      const response = await continuousServiceService.updateAmounts(id, {
        igv_amount: igvNum,
      });
      if (response.success) {
        setToast({ isOpen: true, message: 'Montos ajustados correctamente', type: 'success' });
        setIsEditingAmounts(false);
        loadService();
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al ajustar montos', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al ajustar montos', type: 'error' });
    } finally {
      setSavingAmounts(false);
    }
  };

  // Funciones para importación desde Excel
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setToast({
        isOpen: true,
        message: 'Por favor, selecciona un archivo Excel (.xlsx o .xls)',
        type: 'error'
      });
      return;
    }

    try {
      console.log('📥 Iniciando carga de archivo Excel:', file.name);
      const workbook = new ExcelJS.Workbook();
      const buffer = await file.arrayBuffer();
      await workbook.xlsx.load(buffer);
      console.log('✅ Archivo Excel cargado, número de hojas:', workbook.worksheets.length);
      
      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        console.error('❌ No se encontró ninguna hoja en el archivo');
        setToast({
          isOpen: true,
          message: 'El archivo Excel no contiene hojas de cálculo',
          type: 'error'
        });
        return;
      }

      console.log('📊 Hoja encontrada:', worksheet.name, 'Total de filas:', worksheet.rowCount);

      // Buscar la fila de encabezados (buscar fila que contenga "fecha")
      let headerRowNumber = 1;
      const columnMap = {
        'fecha': 'progress_date',
        'fecha de avance': 'progress_date',
        'servicio': 'service_sku',
        'sku servicio': 'service_sku',
        'sku': 'service_sku',
        'proyecto': 'project_code',
        'codigo proyecto': 'project_code',
        'area': 'area_code',
        'área': 'area_code',
        'codigo area': 'area_code',
        'código área': 'area_code',
        'descripcion': 'description',
        'descripción': 'description',
        'desc': 'description',
        'cantidad': 'quantity',
        'precio unitario': 'unit_price',
        'precio': 'unit_price',
        'tasa igv': 'tax_rate',
        'igv': 'tax_rate',
        'tiene igv': 'tax_rate'
      };

      // Función helper para obtener el valor calculado de una celda (no la fórmula)
      const getCellValue = (cell) => {
        // Si tiene fórmula, usar cell.text que contiene el valor calculado formateado
        if (cell.formula) {
          // cell.text contiene el valor calculado como string
          return cell.text !== undefined && cell.text !== null ? cell.text : cell.value;
        }
        // Si no tiene fórmula, usar cell.value directamente
        return cell.value;
      };

      // Buscar la fila que contiene los encabezados
      console.log('🔍 Buscando fila de encabezados...');
      for (let rowNum = 1; rowNum <= Math.min(10, worksheet.rowCount); rowNum++) {
        const row = worksheet.getRow(rowNum);
        let foundHeader = false;
        row.eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = getCellValue(cell);
          const cellValueStr = cellValue?.toString().toLowerCase().trim() || '';
          if (cellValueStr.includes('fecha') && !foundHeader) {
            headerRowNumber = rowNum;
            foundHeader = true;
            console.log(`✅ Encabezados encontrados en la fila ${rowNum}`);
          }
        });
        if (foundHeader) break;
      }

      // Leer encabezados
      console.log(`📋 Leyendo encabezados de la fila ${headerRowNumber}...`);
      const headerRow = worksheet.getRow(headerRowNumber);
      const headers = [];
      // Leer todas las celdas desde la columna 1
      headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const cellValue = getCellValue(cell);
        headers[colNumber] = cellValue?.toString().toLowerCase().trim() || '';
      });
      console.log('📋 Encabezados encontrados (con índices):', headers);

      const mappedHeaders = {};
      // Iterar sobre todas las columnas encontradas (usando el número de columna real de Excel)
      Object.keys(headers).forEach((colNumberStr) => {
        const colNumber = parseInt(colNumberStr, 10);
        const header = headers[colNumber];
        if (header) {
          // Normalizar header para comparación (minúsculas, sin espacios extra)
          const normalizedHeader = header.toLowerCase().trim();
          console.log(`  🔍 Columna ${colNumber}: Header="${header}", Normalizado="${normalizedHeader}"`);
          
          for (const [key, value] of Object.entries(columnMap)) {
            // Comparar en minúsculas para evitar problemas de mayúsculas/minúsculas y tildes
            if (normalizedHeader.includes(key.toLowerCase())) {
              mappedHeaders[colNumber] = value;
              console.log(`  ✅ Columna ${colNumber} (${header}) mapeada a: ${value}`);
              break;
            }
          }
        }
      });
      console.log('🗺️ Mapeo de columnas:', mappedHeaders);
      console.log('🔍 Verificando mapeo de descripción:', {
        '¿Está mapeada?': Object.values(mappedHeaders).includes('description'),
        'Columna mapeada a description': Object.keys(mappedHeaders).find(k => mappedHeaders[k] === 'description'),
        'Todos los headers': headers
      });

      // Función para parsear fecha (tratando como texto para evitar problemas de zona horaria)
      const parseDate = (dateValue) => {
        if (!dateValue) return null;

        // Si ya viene en formato YYYY-MM-DD, usarlo directamente
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())) {
          return dateValue.trim();
        }

        let date = null;

        // Si es número (fecha serial de Excel)
        if (typeof dateValue === 'number') {
          // Excel cuenta desde 1900-01-01, pero tiene un bug: cuenta 1900 como año bisiesto
          const excelEpoch = new Date(1899, 11, 30);
          date = new Date(excelEpoch.getTime() + dateValue * 86400000);
        }
        // Si ya es un objeto Date
        else if (dateValue instanceof Date) {
          date = dateValue;
        }
        // Si es string, intentar diferentes formatos
        else if (typeof dateValue === 'string') {
          const trimmedValue = dateValue.trim();
          
          // Formato DD/MM/YYYY o DD-MM-YYYY
          const ddmmyyyy = trimmedValue.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
          if (ddmmyyyy) {
            const day = parseInt(ddmmyyyy[1], 10);
            const month = parseInt(ddmmyyyy[2], 10) - 1; // Mes es 0-indexed
            const year = parseInt(ddmmyyyy[3], 10);
            // Crear fecha en timezone local (será interpretada como Lima)
            date = new Date(year, month, day);
          }
          // Formato YYYY-MM-DD
          else {
            const yyyymmdd = trimmedValue.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
            if (yyyymmdd) {
              const year = parseInt(yyyymmdd[1], 10);
              const month = parseInt(yyyymmdd[2], 10) - 1;
              const day = parseInt(yyyymmdd[3], 10);
              date = new Date(year, month, day);
            }
            // Si es solo un número (día del mes), asumir mes y año actual
            else if (/^\d{1,2}$/.test(trimmedValue)) {
              const day = parseInt(trimmedValue, 10);
              if (day >= 1 && day <= 31) {
                const now = new Date();
                const year = now.getFullYear();
                const month = now.getMonth();
                date = new Date(year, month, day);
              }
            }
            // Intentar parseo estándar
            else {
              date = new Date(trimmedValue);
            }
          }
        }

        // Si tenemos una fecha válida, formatearla directamente sin conversión UTC
        if (date && !isNaN(date.getTime())) {
          // Formatear directamente usando los componentes de la fecha local
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }

        return null;
      };

      // Leer datos (desde la fila después de los encabezados)
      console.log(`📊 Iniciando lectura de datos desde la fila ${headerRowNumber + 1} hasta ${worksheet.rowCount}...`);
      const data = [];
      const errors = [];
      
      for (let rowNum = headerRowNumber + 1; rowNum <= worksheet.rowCount; rowNum++) {
        const row = worksheet.getRow(rowNum);
        
        const rowData = {};
        let hasData = false;

        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const mappedKey = mappedHeaders[colNumber];
          
          // Obtener el valor calculado (no la fórmula)
          const cellValue = getCellValue(cell);
          
          // LOG para descripción
          if (mappedKey === 'description') {
            console.log(`  🔍 Fila ${rowNum}, Columna ${colNumber}, DESCRIPCIÓN:`, {
              'mappedKey': mappedKey,
              'cell.value': cell.value,
              'cell.text': cell.text,
              'cell.formula': cell.formula,
              'cellValue (calculado)': cellValue,
              'typeof cellValue': typeof cellValue
            });
          }
          
          // Para descripción, también leer valores vacíos (puede ser opcional)
          const shouldRead = mappedKey && (
            (cellValue !== null && cellValue !== undefined && cellValue !== '') || 
            mappedKey === 'description'
          );
          
          if (shouldRead) {
            // Para fechas, tratar siempre como texto primero para evitar problemas de zona horaria
            if (mappedKey === 'progress_date') {
              // LOG: Datos RAW de Excel antes de cualquier procesamiento
              const rawValue = JSON.stringify(cellValue);
              console.log(`  🔍 Fila ${rowNum}, FECHA RAW DE EXCEL:`, {
                'cellValue (calculado)': cellValue,
                'typeof cellValue': typeof cellValue,
                'cell.type': cell.type,
                'cell.type nombre': ExcelJS.ValueType[cell.type],
                'cellValue instanceof Date': cellValue instanceof Date,
                'cell.text': cell.text,
                'cell.formula': cell.formula,
                'Valor completo': rawValue
              });
              
              // Si es una fecha de Excel, usar el valor completo (ISO string) para evitar problemas de zona horaria
              if (cell.type === ExcelJS.ValueType.Date || cellValue instanceof Date) {
                // Obtener la fecha como objeto Date
                const excelDate = cellValue instanceof Date ? cellValue : new Date(cellValue);
                if (!isNaN(excelDate.getTime())) {
                  // Usar el valor completo (ISO string) si está disponible
                  const isoString = excelDate.toISOString();
                  console.log(`  📅 Fila ${rowNum}, ISO String:`, isoString);
                  
                  // Extraer fecha directamente del ISO string (YYYY-MM-DD)
                  const dateMatch = isoString.match(/^(\d{4})-(\d{2})-(\d{2})/);
                  if (dateMatch) {
                    rowData[mappedKey] = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
                    console.log(`  📅 Fila ${rowNum}, Fecha extraída del ISO:`, rowData[mappedKey]);
                  } else {
                    // Fallback: usar componentes del Date object
                    const year = excelDate.getUTCFullYear();
                    const month = String(excelDate.getUTCMonth() + 1).padStart(2, '0');
                    const day = String(excelDate.getUTCDate()).padStart(2, '0');
                    rowData[mappedKey] = `${year}-${month}-${day}`;
                    console.log(`  📅 Fila ${rowNum}, Fecha usando UTC:`, rowData[mappedKey]);
                  }
                } else {
                  rowData[mappedKey] = String(cellValue).trim();
                  console.log(`  📅 Fila ${rowNum}, Fecha Date (no Date object):`, rowData[mappedKey]);
                }
              } else if (cell.type === ExcelJS.ValueType.Number || typeof cellValue === 'number') {
                console.log(`  📅 Fila ${rowNum}, Number value:`, cellValue);
                // Si es un número (fecha serial de Excel), convertirla
                const excelEpoch = new Date(1899, 11, 30);
                const date = new Date(excelEpoch.getTime() + cellValue * 86400000);
                // Usar UTC para evitar problemas de zona horaria
                const year = date.getUTCFullYear();
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const day = String(date.getUTCDate()).padStart(2, '0');
                rowData[mappedKey] = `${year}-${month}-${day}`;
                console.log(`  📅 Fila ${rowNum}, Fecha Number convertida (UTC):`, rowData[mappedKey]);
              } else {
                // Si es texto, usarlo directamente
                rowData[mappedKey] = String(cellValue).trim();
                console.log(`  📅 Fila ${rowNum}, Fecha como texto:`, rowData[mappedKey]);
              }
            } else {
              // Para descripción, convertir a string y trim
              if (mappedKey === 'description') {
                // Leer descripción incluso si está vacía
                rowData[mappedKey] = (cellValue !== null && cellValue !== undefined) ? String(cellValue).trim() : '';
                console.log(`  📝 Fila ${rowNum}, Descripción leída (calculado: ${cellValue}):`, rowData[mappedKey]);
              } else if (mappedKey === 'service_sku' || mappedKey === 'project_code' || mappedKey === 'area_code') {
                // Convertir SKU, código de proyecto y código de área a string
                rowData[mappedKey] = (cellValue !== null && cellValue !== undefined) ? String(cellValue).trim() : '';
              } else {
                // Para otros campos, usar el valor calculado
                rowData[mappedKey] = cellValue;
              }
            }
            hasData = true;
          }
        });

        if (!hasData) {
          console.log(`  ⏭️ Fila ${rowNum}: Sin datos, saltando...`);
          continue; // Saltar filas vacías
        }

        console.log(`  📝 Fila ${rowNum}: Datos leídos:`, rowData);

        // Validar y procesar datos
        const processedRow = {
          rowNumber: rowNum,
          original: rowData
        };

        // Validar y procesar fecha
        if (!rowData.progress_date) {
          console.log(`  ❌ Fila ${rowNum}: Fecha no encontrada`);
          errors.push({
            row: rowNum,
            field: 'fecha',
            message: 'La fecha es requerida'
          });
          processedRow.error = true;
        } else {
          console.log(`  🔍 Fila ${rowNum}: Procesando fecha:`, rowData.progress_date, 'Tipo:', typeof rowData.progress_date);
          const parsedDate = parseDate(rowData.progress_date);
          console.log(`  📅 Fila ${rowNum}: Fecha parseada:`, parsedDate);
          if (!parsedDate) {
            console.log(`  ❌ Fila ${rowNum}: Error al parsear fecha`);
            errors.push({
              row: rowNum,
              field: 'fecha',
              message: `Formato de fecha inválido: ${rowData.progress_date}. Use DD/MM/YYYY o formato de fecha de Excel`
            });
            processedRow.error = true;
          } else {
            processedRow.progress_date = parsedDate;
            console.log(`  ✅ Fila ${rowNum}: Fecha procesada correctamente:`, parsedDate);
          }
        }

        // Buscar servicio solo por SKU
        if (rowData.service_sku) {
          const searchValue = String(rowData.service_sku || '').trim();
          console.log(`  🔍 Fila ${rowNum}: Buscando servicio por SKU:`, searchValue);
          console.log(`  📋 Total de servicios disponibles:`, services.length);
          console.log(`  📋 Primeros 5 servicios:`, services.slice(0, 5).map(s => ({ id: s.id, sku: s.sku, name: s.name })));
          
          const service = services.find(s => {
            const serviceSku = String(s.sku || '').trim();
            const skuMatch = serviceSku.toLowerCase() === searchValue.toLowerCase();
            
            if (skuMatch) {
              console.log(`  ✅ Fila ${rowNum}: Servicio encontrado por SKU:`, {
                buscado: searchValue,
                encontrado: { id: s.id, sku: s.sku, name: s.name }
              });
            }
            
            return skuMatch;
          });
          
          if (service) {
            processedRow.service_id = service.id;
            console.log(`  ✅ Fila ${rowNum}: Servicio asignado ID:`, service.id);
          } else {
            console.log(`  ❌ Fila ${rowNum}: Servicio NO encontrado por SKU`);
            console.log(`  🔍 Búsqueda realizada:`, {
              buscado_sku: searchValue,
              servicios_disponibles: services.map(s => ({ sku: s.sku, name: s.name }))
            });
            errors.push({
              row: rowNum,
              field: 'servicio',
              message: `Servicio no encontrado con SKU: ${searchValue}`
            });
          }
        } else {
          console.log(`  ⚠️ Fila ${rowNum}: No se proporcionó SKU de servicio`);
          errors.push({
            row: rowNum,
            field: 'servicio',
            message: 'El SKU del servicio es requerido'
          });
          processedRow.error = true;
        }

        // Buscar proyecto por código
        if (rowData.project_code) {
          const projectCode = String(rowData.project_code || '').trim();
          const project = projects.find(p => {
            const pCode = String(p.code || '').trim();
            return pCode.toLowerCase() === projectCode.toLowerCase();
          });
          if (project) {
            processedRow.project_id = project.id;
          } else {
            errors.push({
              row: rowNum,
              field: 'proyecto',
              message: `Proyecto no encontrado: ${rowData.project_code}`
            });
          }
        }

        // Buscar área por código (solo si el servicio tiene include_areas; areas está cargado en ese caso)
        if (service?.include_areas && rowData.area_code) {
          const areaCode = String(rowData.area_code || '').trim();
          const area = areas.find(a => {
            const aCode = String(a.code || '').trim();
            return aCode.toLowerCase() === areaCode.toLowerCase();
          });
          if (area) {
            processedRow.area_id = area.id;
          } else {
            errors.push({
              row: rowNum,
              field: 'area',
              message: `Área no encontrada: ${rowData.area_code}`
            });
          }
        }

        // Validar cantidad
        if (!rowData.quantity || isNaN(parseFloat(rowData.quantity)) || parseFloat(rowData.quantity) <= 0) {
          errors.push({
            row: rowNum,
            field: 'cantidad',
            message: 'La cantidad debe ser mayor a 0'
          });
          processedRow.error = true;
        } else {
          processedRow.quantity = roundTo4(rowData.quantity);
        }

        // Validar precio unitario
        if (!rowData.unit_price || isNaN(parseFloat(rowData.unit_price)) || parseFloat(rowData.unit_price) < 0) {
          errors.push({
            row: rowNum,
            field: 'precio unitario',
            message: 'El precio unitario es requerido y debe ser mayor o igual a 0'
          });
          processedRow.error = true;
        } else {
          processedRow.unit_price = roundTo4(rowData.unit_price);
        }

        // Procesar tasa de IGV
        if (rowData.tax_rate !== undefined && rowData.tax_rate !== null && rowData.tax_rate !== '') {
          const taxRate = parseFloat(rowData.tax_rate);
          if ([0, 10, 10.5, 18].includes(taxRate)) {
            processedRow.tax_rate = taxRate;
          } else {
            processedRow.tax_rate = 18; // Default
          }
        } else {
          processedRow.tax_rate = 18; // Default
        }

        // Descripción
        processedRow.description = rowData.description || null;
        console.log(`  📝 Fila ${rowNum}: Descripción:`, rowData.description, '→', processedRow.description);

        console.log(`  ✅ Fila ${rowNum}: Registro procesado:`, processedRow);
        data.push(processedRow);
      }

      console.log('📊 Resumen de importación:');
      console.log('  - Total de registros procesados:', data.length);
      console.log('  - Total de errores:', errors.length);
      console.log('  - Registros válidos:', data.filter(d => !d.error).length);
      console.log('  - Registros con errores:', data.filter(d => d.error).length);
      console.log('  - Datos completos:', data);
      console.log('  - Errores completos:', errors);

      setImportData(data);
      setImportErrors(errors);

      if (data.length === 0) {
        console.warn('⚠️ No se encontraron datos válidos');
        setToast({
          isOpen: true,
          message: 'No se encontraron datos válidos en el archivo Excel',
          type: 'warning'
        });
      } else {
        console.log('✅ Datos cargados exitosamente');
        setToast({
          isOpen: true,
          message: `Se encontraron ${data.length} registro(s) en el archivo. ${errors.length > 0 ? `${errors.length} error(es) encontrado(s).` : ''}`,
          type: errors.length > 0 ? 'warning' : 'success'
        });
      }

    } catch (error) {
      console.error('❌ Error al leer archivo Excel:', error);
      console.error('❌ Stack trace:', error.stack);
      setToast({
        isOpen: true,
        message: 'Error al leer el archivo Excel: ' + error.message,
        type: 'error'
      });
    }
  };

  const handleImportSubmit = async () => {
    if (importData.length === 0) {
      setToast({
        isOpen: true,
        message: 'No hay datos para importar',
        type: 'warning'
      });
      return;
    }

    // Filtrar solo los registros sin errores
    const validData = importData
      .filter(item => !item.error)
      .map(item => ({
        progress_date: item.progress_date,
        service_id: item.service_id || null,
        project_id: item.project_id || null,
        area_id: item.area_id || null,
        description: item.description || null,
        quantity: roundTo4(item.quantity),
        unit_price: roundTo4(item.unit_price),
        tax_rate: item.tax_rate
      }));

    if (validData.length === 0) {
      setToast({
        isOpen: true,
        message: 'No hay registros válidos para importar',
        type: 'error'
      });
      return;
    }

    setImportLoading(true);
    try {
      const response = await continuousServiceService.importProgress(id, { progress: validData });
      
      if (response.success) {
        setToast({
          isOpen: true,
          message: response.message || `Se importaron ${response.data?.successful || 0} registro(s) exitosamente`,
          type: 'success'
        });
        setIsImportModalOpen(false);
        setImportData([]);
        setImportErrors([]);
        loadService();
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al importar los registros',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al importar registros:', error);
      setToast({
        isOpen: true,
        message: 'Error al importar los registros de avance',
        type: 'error'
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Registros de Avance');

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4472C4' }
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const warningStyle = {
        font: { bold: true, color: { argb: 'FFFF0000' }, size: 11 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFE6E6' }
        },
        alignment: { horizontal: 'left', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const infoStyle = {
        font: { italic: true, color: { argb: 'FF666666' }, size: 10 },
        alignment: { horizontal: 'left' }
      };

      const includeAreasInTemplate = !!service?.include_areas;
      const mergeRange = includeAreasInTemplate ? 'H' : 'G';

      // Información de la plantilla
      worksheet.mergeCells(`A1:${mergeRange}1`);
      const titleRow = worksheet.getRow(1);
      titleRow.height = 25;
      titleRow.getCell(1).value = 'PLANTILLA DE IMPORTACIÓN - REGISTROS DE AVANCE';
      titleRow.getCell(1).font = { size: 14, bold: true };
      titleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

      // ADVERTENCIA IMPORTANTE
      worksheet.mergeCells(`A2:${mergeRange}2`);
      const warningRow = worksheet.getRow(2);
      warningRow.height = 30;
      warningRow.getCell(1).value = '⚠️ IMPORTANTE: ANTES DE IMPORTAR, DEBE BORRAR ESTAS INSTRUCCIONES Y LOS DATOS DE EJEMPLO. SOLO DEJE LOS ENCABEZADOS Y SUS DATOS.';
      warningRow.getCell(1).style = warningStyle;
      warningRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };

      worksheet.mergeCells(`A3:${mergeRange}3`);
      const infoRow1 = worksheet.getRow(3);
      infoRow1.getCell(1).value = 'Instrucciones: Complete las columnas según se indica. Las columnas marcadas con * son obligatorias.';
      infoRow1.getCell(1).style = infoStyle;

      worksheet.mergeCells(`A4:${mergeRange}4`);
      const infoRow2 = worksheet.getRow(4);
      infoRow2.getCell(1).value = 'Fecha: Formato DD/MM/YYYY o fecha de Excel | Servicio: SKU o Nombre del servicio | Proyecto: Código del proyecto (opcional)' + (includeAreasInTemplate ? ' | Área: Código del área (opcional)' : '');
      infoRow2.getCell(1).style = infoStyle;

      worksheet.mergeCells(`A5:${mergeRange}5`);
      const infoRow3 = worksheet.getRow(5);
      infoRow3.getCell(1).value = 'Tasa IGV: 0, 10, 10.5 o 18 (opcional, default: 18). Cantidad y Precio unitario aceptan hasta 4 decimales.';
      infoRow3.getCell(1).style = infoStyle;

      // Encabezados
      const headerRow = worksheet.getRow(6);
      const headers = [
        'Fecha *',
        'Servicio (SKU o Nombre) *',
        'Proyecto (Código)',
        ...(includeAreasInTemplate ? ['Área (Código)'] : []),
        'Descripción',
        'Cantidad *',
        'Precio Unitario *',
        'Tasa IGV (0/10/10.5/18)'
      ];

      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = headerStyle;
      });

      // Datos de ejemplo
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const exampleRow1 = [
        today,
        'Ejemplo: SKU-001 o Nombre del Servicio - BORRAR ESTA FILA',
        'PROY-001',
        ...(includeAreasInTemplate ? ['AREA-001'] : []),
        'Descripción del avance del día - BORRAR ESTA FILA',
        1,
        100.00,
        18
      ];
      const exampleRow2 = [
        tomorrow,
        'Ejemplo: SKU-002 - BORRAR ESTA FILA',
        '',
        ...(includeAreasInTemplate ? [''] : []),
        'Otro registro de ejemplo - BORRAR ESTA FILA',
        2,
        50.00,
        10
      ];
      const exampleData = [exampleRow1, exampleRow2];

      exampleData.forEach((row, rowIndex) => {
        const dataRow = worksheet.getRow(7 + rowIndex);
        row.forEach((value, colIndex) => {
          const cell = dataRow.getCell(colIndex + 1);
          if (colIndex === 0) {
            cell.value = value;
            cell.numFmt = 'dd/mm/yyyy';
          } else {
            cell.value = value;
          }
          const qtyCol = includeAreasInTemplate ? 5 : 4;
          const priceCol = includeAreasInTemplate ? 6 : 5;
          if (colIndex === qtyCol || colIndex === priceCol) {
            cell.numFmt = '#,##0.00';
          }
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          if (colIndex === 1 || colIndex === (includeAreasInTemplate ? 4 : 3)) {
            cell.font = { italic: true, color: { argb: 'FF999999' } };
          }
        });
      });

      // Ajustar ancho de columnas
      worksheet.columns = [
        { width: 15 }, // Fecha
        { width: 35 }, // Servicio
        { width: 20 }, // Proyecto
        ...(includeAreasInTemplate ? [{ width: 18 }] : []), // Área
        { width: 40 }, // Descripción
        { width: 12 }, // Cantidad
        { width: 18 }, // Precio Unitario
        { width: 15 }  // Tasa IGV
      ];

      // Congelar filas de encabezado (fila 6)
      worksheet.views = [
        {
          state: 'frozen',
          ySplit: 6
        }
      ];

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Plantilla_Importacion_Registros_Avance_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);

      setToast({
        isOpen: true,
        message: 'Plantilla descargada exitosamente',
        type: 'success'
      });
    } catch (error) {
      console.error('Error al generar plantilla:', error);
      setToast({
        isOpen: true,
        message: 'Error al generar la plantilla',
        type: 'error'
      });
    }
  };

  // Calcular totales generales: base imponible = suma de subtotales por grupo; IGV = base × tasa
  const calculateServiceTotals = () => {
    let baseImponible18 = 0;
    let baseImponible10 = 0;
    let baseImponible10_5 = 0;
    let noGravados = 0;

    progress.forEach(item => {
      const subtotal = parseFloat(item.subtotal || 0);
      const taxRate = parseFloat(item.tax_rate || 0);

      if (Math.abs(taxRate - 18) < 0.01) {
        baseImponible18 += subtotal;
      } else if (Math.abs(taxRate - 10) < 0.01) {
        baseImponible10 += subtotal;
      } else if (Math.abs(taxRate - 10.5) < 0.01) {
        baseImponible10_5 += subtotal;
      } else {
        noGravados += subtotal;
      }
    });

    const igv18 = Math.round(baseImponible18 * 0.18 * 100) / 100;
    const igv10 = Math.round(baseImponible10 * 0.1 * 100) / 100;
    const igv10_5 = Math.round(baseImponible10_5 * 0.105 * 100) / 100;
    const total = baseImponible18 + igv18 + baseImponible10 + igv10 + baseImponible10_5 + igv10_5 + noGravados;

    return {
      baseImponible18,
      baseImponible10,
      baseImponible10_5,
      noGravados,
      igv18,
      igv10,
      igv10_5,
      total
    };
  };

  const totals = calculateProgressTotals();
  const serviceTotals = calculateServiceTotals();

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-gray-500">Cargando servicio continuo...</div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Servicio continuo no encontrado</p>
          <Link to="/continuous-services" className="mt-4 inline-block text-teal-600 hover:text-teal-700">
            Volver a la lista
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Servicio Continuo: {service.code || `SC-${service.id}`}
              </h1>
              <p className="text-gray-600 mt-1">Detalle y registros de avance del servicio</p>
            </div>
            <div className="flex gap-2">
              {service.status === 'open' && (
                <>
                  <button
                    onClick={() => setIsImportModalOpen(true)}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium transition-colors"
                    title="Importar desde Excel"
                  >
                    📥 Importar Excel
                  </button>
                  <button
                    onClick={() => setIsCloseModalOpen(true)}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md font-medium transition-colors"
                  >
                    Cerrar Servicio
                  </button>
                </>
              )}
              {service.status === 'closed' && (
                <button
                  onClick={handleOpenService}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors"
                  title="Abrir Servicio"
                  disabled={loadingAction}
                >
                  📄 Abrir Servicio
                </button>
              )}
              <button
                onClick={() => {
                  const printUrl = `/continuous-services/${id}/print`;
                  window.open(printUrl, '_blank');
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                title="Imprimir"
              >
                🖨️ Imprimir
              </button>
              <Link
                to="/continuous-services"
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors"
              >
                Volver
              </Link>
            </div>
          </div>
        </div>

        {/* Información General (cabeceras) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Información General</h2>
            {service.status !== 'closed' && (
            !isEditingHeader ? (
              <button
                type="button"
                onClick={() => {
                  const toInputDate = (d) => {
                    if (!d) return '';
                    const s = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10) : formatDateToLima(d);
                    return s;
                  };
                  setHeaderForm({
                    start_date: toInputDate(service.start_date),
                    end_date: toInputDate(service.end_date),
                    notes: service.notes || '',
                    include_areas: !!service.include_areas
                  });
                  setIsEditingHeader(true);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md font-medium transition-colors text-sm"
              >
                Editar
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={savingHeader}
                  onClick={async () => {
                    setSavingHeader(true);
                    try {
                      const response = await continuousServiceService.updateContinuousService(id, {
                        start_date: headerForm.start_date || undefined,
                        end_date: headerForm.end_date || undefined,
                        notes: headerForm.notes || undefined,
                        include_areas: headerForm.include_areas
                      });
                      if (response.success) {
                        setService(response.data);
                        setIsEditingHeader(false);
                        setToast({ isOpen: true, message: 'Información actualizada', type: 'success' });
                      } else {
                        setToast({ isOpen: true, message: response.message || 'Error al guardar', type: 'error' });
                      }
                    } catch (error) {
                      setToast({ isOpen: true, message: 'Error al actualizar la información', type: 'error' });
                    } finally {
                      setSavingHeader(false);
                    }
                  }}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors text-sm disabled:opacity-50"
                >
                  {savingHeader ? 'Guardando...' : 'Guardar'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditingHeader(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors text-sm"
                >
                  Cancelar
                </button>
              </div>
            )
            )}
          </div>
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-600">Proveedor</label>
              <p className="text-gray-900 font-medium">{service.supplier?.name || 'N/A'}</p>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-600">Fecha de Inicio</label>
              {isEditingHeader ? (
                <input
                  type="date"
                  value={headerForm.start_date}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                />
              ) : (
                <p className="text-gray-900">
                  {service.start_date ? formatDateLocaleLima(service.start_date) : 'N/A'}
                </p>
              )}
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-600">Fecha de Fin</label>
              {isEditingHeader ? (
                <input
                  type="date"
                  value={headerForm.end_date}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                />
              ) : (
                <p className="text-gray-900">
                  {service.end_date ? formatDateLocaleLima(service.end_date) : '-'}
                </p>
              )}
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-600">Estado</label>
              <div className="mt-1">{getStatusBadge(service.status)}</div>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-gray-600">Observaciones</label>
              {isEditingHeader ? (
                <input
                  type="text"
                  value={headerForm.notes}
                  onChange={(e) => setHeaderForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Observaciones"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-teal-500"
                />
              ) : (
                <p className="text-gray-900">{service.notes || '-'}</p>
              )}
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="text-sm font-medium text-gray-600">Incluir áreas</label>
              {isEditingHeader ? (
                <div className="mt-1 flex items-center">
                  <input
                    type="checkbox"
                    id="header_include_areas"
                    checked={!!headerForm.include_areas}
                    onChange={(e) => setHeaderForm(prev => ({ ...prev, include_areas: e.target.checked }))}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="header_include_areas" className="ml-2 text-sm text-gray-700">Sí</label>
                </div>
              ) : (
                <p className="text-gray-900">{service.include_areas ? 'Sí' : 'No'}</p>
              )}
            </div>
          </div>
        </div>

        {/* Registros de Avance */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-800">Registros de Avance</h2>
            {service.status === 'open' && (
              <button
                onClick={handleAddProgress}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors text-sm"
              >
                + Agregar Registro
              </button>
            )}
          </div>

          {progress.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay registros de avance</p>
              {service.status === 'open' && (
                <button
                  onClick={handleAddProgress}
                  className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
                >
                  Agregar el primero
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proyecto</th>
                    {service.include_areas && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Área</th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tasa IGV</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                    {service.status === 'open' && (
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {progress.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatDateLocaleLima(item.progress_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.service ? (
                          <div>
                            <div className="font-medium">{item.service.name}</div>
                            {item.service.sku && (
                              <div className="text-xs text-gray-500">SKU: {item.service.sku}</div>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.project ? (
                          <div>
                            <div className="font-medium">{item.project.code}</div>
                            <div className="text-xs text-gray-500">{item.project.name}</div>
                          </div>
                        ) : '-'}
                      </td>
                      {service.include_areas && (
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.area ? (
                            <div>
                              <div className="font-medium">{item.area.code}</div>
                              <div className="text-xs text-gray-500">{item.area.name}</div>
                            </div>
                          ) : '-'}
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {item.description || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {Number(item.quantity).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency4(item.unit_price)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency4(item.subtotal)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {Math.abs(parseFloat(item.tax_rate || 0) - 18) < 0.01 ? (
                          '18%'
                        ) : Math.abs(parseFloat(item.tax_rate || 0) - 10) < 0.01 ? (
                          '10%'
                        ) : Math.abs(parseFloat(item.tax_rate || 0) - 10.5) < 0.01 ? (
                          '10.5%'
                        ) : (
                          'No gravado'
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatCurrency(item.total)}
                      </td>
                      {service.status === 'open' && (
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleEditProgress(item)}
                              className="text-teal-600 hover:text-teal-900"
                            >
                              Editar
                            </button>
                            <button
                              onClick={() => handleDeleteProgress(item.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Eliminar
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Totales */}
          {progress.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-full md:w-80 space-y-2">
                  {serviceTotals.baseImponible18 > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Imponible (18%):</span>
                      <span className="font-medium text-gray-700">
                        {formatCurrency(serviceTotals.baseImponible18)}
                      </span>
                    </div>
                  )}
                  {serviceTotals.baseImponible10 > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Imponible (10%):</span>
                      <span className="font-medium text-gray-700">
                        {formatCurrency(serviceTotals.baseImponible10)}
                      </span>
                    </div>
                  )}
                  {serviceTotals.baseImponible10_5 > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Base Imponible (10.5%):</span>
                      <span className="font-medium text-gray-700">
                        {formatCurrency(serviceTotals.baseImponible10_5)}
                      </span>
                    </div>
                  )}
                  {serviceTotals.noGravados > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">No Gravados:</span>
                      <span className="font-medium text-gray-700">
                        {formatCurrency(serviceTotals.noGravados)}
                      </span>
                    </div>
                  )}
                  {/* Un solo IGV: valor guardado (ajustado si se usó Ajustar montos) o calculado */}
                  {!isEditingAmounts || service?.status === 'closed' ? (
                    <>
                      <div className="flex justify-between text-sm pt-1">
                        <span className="text-gray-600">IGV:</span>
                        <span className="font-medium text-gray-700">
                          {formatCurrency(service?.igv_amount != null ? Number(service.igv_amount) : serviceTotals.igv18 + serviceTotals.igv10 + (serviceTotals.igv10_5 || 0))}
                        </span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-gray-300">
                        <span className="text-base font-semibold text-gray-800">Total:</span>
                        <span className="text-2xl font-bold text-teal-600">
                          {formatCurrency(service?.total_amount != null ? Number(service.total_amount) : serviceTotals.total)}
                        </span>
                      </div>
                      {service?.status === 'open' && (
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={handleOpenAmounts}
                            className="text-sm text-teal-600 hover:text-teal-700 font-medium"
                            title="Ajustar IGV por diferencias de decimales"
                          >
                            Ajustar montos
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="pt-2 border-t border-gray-300 space-y-3">
                      <p className="text-xs text-gray-500">Modifique el IGV para corregir la diferencia por decimales. El Total se recalcula automáticamente (Base + IGV + No gravados).</p>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">IGV</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={amountForm.igv_amount}
                          onChange={(e) => setAmountForm(prev => ({ ...prev, igv_amount: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Total (recalculado)</label>
                        <p className="text-sm font-semibold text-teal-600 py-1.5">
                          {formatCurrency(
                            (Number(service?.base_imponible) || serviceTotals.baseImponible18 + serviceTotals.baseImponible10 + (serviceTotals.baseImponible10_5 || 0)) +
                            (Number(amountForm.igv_amount) || 0) +
                            (Number(service?.no_gravados) || serviceTotals.noGravados)
                          )}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSaveAmounts}
                          disabled={savingAmounts}
                          className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium rounded-md"
                        >
                          {savingAmounts ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditingAmounts(false)}
                          className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-md"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Agregar/Editar Registro de Avance */}
      {(isAddProgressModalOpen || isEditProgressModalOpen) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isEditProgressModalOpen ? 'Editar Registro de Avance' : 'Registrar Avance Diario'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={progressForm.progress_date}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, progress_date: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      progressErrors.progress_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {progressErrors.progress_date && (
                    <p className="mt-1 text-sm text-red-600">{progressErrors.progress_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Servicio
                  </label>
                  <ServiceSelector
                    value={progressForm.service_id}
                    onChange={(value) => setProgressForm(prev => ({ ...prev, service_id: value }))}
                    services={services}
                    error={progressErrors.service_id}
                    showSku={true}
                  />
                  {progressErrors.service_id && (
                    <p className="mt-1 text-sm text-red-600">{progressErrors.service_id}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Proyecto
                  </label>
                  <select
                    value={progressForm.project_id}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, project_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Seleccionar proyecto (opcional)</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.code} - {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                {service?.include_areas && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Área
                    </label>
                    <select
                      value={progressForm.area_id}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, area_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="">Seleccionar área (opcional)</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.id}>
                          {area.code} - {area.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción
                  </label>
                  <textarea
                    value={progressForm.description}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Descripción del avance del día..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cantidad <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    pattern="^\d*([.]\d{0,4})?$"
                    value={progressForm.quantity}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, quantity: sanitizeDecimalInput(e.target.value) }))}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      progressErrors.quantity ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {progressErrors.quantity && (
                    <p className="mt-1 text-sm text-red-600">{progressErrors.quantity}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio Unitario <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">S/</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="^\d*([.]\d{0,4})?$"
                      value={progressForm.unit_price}
                      onChange={(e) => setProgressForm(prev => ({ ...prev, unit_price: sanitizeDecimalInput(e.target.value) }))}
                      className={`w-full pl-8 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                        progressErrors.unit_price ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {progressErrors.unit_price && (
                    <p className="mt-1 text-sm text-red-600">{progressErrors.unit_price}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tasa de IGV
                  </label>
                  <select
                    value={progressForm.tax_rate}
                    onChange={(e) => setProgressForm(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="0">No tiene IGV</option>
                    <option value="10">10%</option>
                    <option value="10.5">10.5%</option>
                    <option value="18">18%</option>
                  </select>
                </div>

                {/* Totales calculados */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <label className="text-gray-600">Subtotal</label>
                      <p className="font-medium text-gray-900">{formatCurrency4(totals.subtotal)}</p>
                    </div>
                    <div>
                      <label className="text-gray-600">
                        IGV {totals.taxRate > 0 ? `(${totals.taxRate}%)` : ''}
                      </label>
                      <p className="font-medium text-gray-900">{formatCurrency(totals.taxAmount)}</p>
                    </div>
                    <div>
                      <label className="text-gray-600">Total</label>
                      <p className="font-bold text-teal-700 text-lg">{formatCurrency(totals.total)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddProgressModalOpen(false);
                    setIsEditProgressModalOpen(false);
                    setSelectedProgress(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                  disabled={loadingAction}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSaveProgress}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                  disabled={loadingAction}
                >
                  {loadingAction ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Cerrar Servicio (pide fecha de cierre) */}
      <CloseServiceModal
        isOpen={isCloseModalOpen}
        onClose={() => setIsCloseModalOpen(false)}
        onConfirm={handleCloseService}
        serviceName={service?.code || (service ? `SC-${service.id}` : '')}
        loading={loadingAction}
      />

      {/* Modal: Eliminar Registro de Avance */}
      <ConfirmModal
        isOpen={isDeleteProgressModalOpen}
        onClose={() => {
          setIsDeleteProgressModalOpen(false);
          setProgressToDelete(null);
        }}
        onConfirm={handleConfirmDeleteProgress}
        title="Eliminar Registro de Avance"
        message="¿Estás seguro de eliminar este registro de avance? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        confirmColor="red"
        loading={loadingAction}
      />

      {/* Modal: Generar Documento (placeholder - se implementará después) */}
      {isGenerateDocumentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Generar Documento Final
              </h3>
              <p className="text-gray-700 mb-4">
                Esta funcionalidad se implementará próximamente. El servicio pasará a documentos como una orden de servicio.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setIsGenerateDocumentModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Importar desde Excel */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Importar Registros de Avance desde Excel
              </h3>

              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Seleccionar archivo Excel
                  </label>
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors text-sm"
                  >
                    📥 Descargar Plantilla
                  </button>
                </div>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <p className="mt-2 text-xs text-gray-500">
                  El archivo debe contener las siguientes columnas: Fecha, Servicio (SKU o Nombre), Proyecto (Código, opcional), 
                  Descripción (opcional), Cantidad, Precio Unitario, Tasa IGV (0, 10, 10.5 o 18, opcional, default: 18)
                </p>
              </div>

              {importData.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-800">
                      Vista Previa de Datos ({importData.length} registro(s))
                    </h4>
                    {importErrors.length > 0 && (
                      <span className="text-sm text-red-600 font-medium">
                        {importErrors.length} error(es) encontrado(s)
                      </span>
                    )}
                  </div>

                  <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fila</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Servicio</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proyecto</th>
                          {service?.include_areas && (
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Área</th>
                          )}
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Precio Unit.</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tasa IGV</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {importData.map((item, index) => {
                          const rowErrors = importErrors.filter(e => e.row === item.rowNumber);
                          const serviceItem = services.find(s => s.id === item.service_id);
                          const projectItem = projects.find(p => p.id === item.project_id);
                          const areaItem = areas.find(a => a.id === item.area_id);
                          return (
                            <tr key={index} className={item.error ? 'bg-red-50' : 'hover:bg-gray-50'}>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.rowNumber}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {item.progress_date ? (() => {
                                  if (typeof item.progress_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(item.progress_date)) {
                                    const [year, month, day] = item.progress_date.split('-');
                                    return `${day}/${month}/${year}`;
                                  }
                                  return formatDateLocaleLima(item.progress_date);
                                })() : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {serviceItem ? serviceItem.name : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {projectItem ? projectItem.code : '-'}
                              </td>
                              {service?.include_areas && (
                                <td className="px-3 py-2 text-sm text-gray-900">
                                  {areaItem ? `${areaItem.code} - ${areaItem.name}` : '-'}
                                </td>
                              )}
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {item.description || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.quantity != null ? Number(item.quantity).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) : '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {item.unit_price != null ? formatCurrency4(roundTo4(item.unit_price)) : '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">{item.tax_rate || 18}%</td>
                              <td className="px-3 py-2 text-sm">
                                {item.error || rowErrors.length > 0 ? (
                                  <span className="text-red-600 font-medium">Error</span>
                                ) : (
                                  <span className="text-green-600 font-medium">✓ Válido</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {importErrors.length > 0 && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h5 className="text-sm font-semibold text-red-800 mb-2">Errores encontrados:</h5>
                      <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                        {importErrors.map((error, index) => (
                          <li key={index}>
                            Fila {error.row}: {error.field} - {error.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setIsImportModalOpen(false);
                    setImportData([]);
                    setImportErrors([]);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                  disabled={importLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleImportSubmit}
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors disabled:opacity-50"
                  disabled={importLoading || importData.length === 0 || importData.every(item => item.error)}
                >
                  {importLoading ? 'Importando...' : 'Importar Registros'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ContinuousServiceDetail;
